import {
  CURRENT_SURFACE,
  emptyPrefs,
  emptySurfacePrefs,
  LEGACY_FAVORITES_KEY,
  LEGACY_LAST_CATEGORY_KEY,
  PREFS_VERSION,
} from "./constants";
import { FavoriteRef, FavoriteType, Prefs, Surface } from "./types";

function isObject(v: unknown): v is Record<string, any> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Ordered migration steps. `STEPS[n]` migrates a blob at version `n` to version `n+1`.
 * When you bump {@link PREFS_VERSION}, append exactly one function here; old stored
 * blobs (LocalStorage or server) then upgrade lazily on the next load.
 */
const STEPS: Array<(raw: Record<string, any>) => Record<string, any>> = [
  // v0 (legacy / unversioned) -> v1: establish the canonical top-level shape.
  (raw) => ({
    version: 1,
    settings: isObject(raw.settings) ? raw.settings : {},
    bySurface: isObject(raw.bySurface) ? raw.bySurface : {},
  }),
];

/** Normalizes any stored/served blob to the current {@link Prefs} shape and version. */
export function migrate(raw: unknown): Prefs {
  if (!isObject(raw)) {
    return emptyPrefs();
  }
  let current: Record<string, any> = { ...raw };
  let version = typeof current.version === "number" ? current.version : 0;
  while (version < PREFS_VERSION && STEPS[version]) {
    current = STEPS[version](current);
    version = typeof current.version === "number" ? current.version : version + 1;
  }
  return normalize(current);
}

/** Defensive shape guarantees, independent of version, so callers never crash. */
function normalize(raw: Record<string, any>): Prefs {
  const prefs = emptyPrefs();
  prefs.settings = isObject(raw.settings) ? raw.settings : {};
  const bySurface = isObject(raw.bySurface) ? raw.bySurface : {};
  for (const [surface, value] of Object.entries(bySurface)) {
    const sp = isObject(value) ? value : {};
    prefs.bySurface[surface as Surface] = {
      ...emptySurfacePrefs(),
      ...sp,
      favorites: normalizeFavorites(sp.favorites),
    };
  }
  return prefs;
}

function normalizeFavorites(raw: unknown): FavoriteRef[] {
  if (!Array.isArray(raw)) return [];
  const out: FavoriteRef[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      // Legacy bare-id favorite; type is unknown here and self-heals on catalog load.
      out.push({ type: inferType(entry), id: entry });
    } else if (isObject(entry) && typeof entry.id === "string") {
      out.push({ type: (entry.type as FavoriteType) || inferType(entry.id), id: entry.id });
    }
  }
  return out;
}

/** Best-effort type inference for legacy bare ids; reconciled from real data later. */
function inferType(id: string): FavoriteType {
  // Workflow/playbook ids are UUID-like; mock tool slugs are hyphenated words.
  return /^[0-9a-f]{8}-/i.test(id) ? "workflow" : "tool";
}

/** Builds a current-version envelope from the first-generation LocalStorage keys. */
export function importLegacyLocal(): Prefs | null {
  try {
    const favRaw = localStorage.getItem(LEGACY_FAVORITES_KEY);
    const lastCat = localStorage.getItem(LEGACY_LAST_CATEGORY_KEY);
    if (favRaw == null && lastCat == null) {
      return null;
    }
    const prefs = emptyPrefs();
    const sp = emptySurfacePrefs();
    sp.favorites = normalizeFavorites(favRaw ? JSON.parse(favRaw) : []);
    if (lastCat) {
      sp.lastCategory = lastCat;
    }
    prefs.bySurface[CURRENT_SURFACE] = sp;
    return prefs;
  } catch {
    return null;
  }
}
